import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import type {OverlayPanel} from "primereact/overlaypanel";
import type {MouseEvent} from "react";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {matchPath, useLocation} from "react-router-dom";

import MissingPermissionTooltip from "../component/MissingPermissionTooltip";
import {useNavigateWithQuery} from "../hooks/navigationHooks";
import {useCheckPermissionRequirement} from "../hooks/permissionHooks";
import {selectPurposePolicy, selectRevaId} from "../redux/persistenceSlice";
import {useRevaSelector} from "../redux/revaHooks";
import type {PlainRevaSelector} from "../redux/store";
import {selectDarkTheme} from "../redux/themeSlice";
import {selectAnyValueAvailable} from "../redux/valueIndicationSelectors";
import {selectHasValueOrders} from "../redux/valueOrdersSlice";
import {
    PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS,
    PERMISSION_RULE_CHAT,
    PERMISSION_RULE_LOCATION,
    PERMISSION_RULE_MARKET,
    type PermissionRequirement,
} from "../util/permissions";
import {ResetSessionPopup} from "./widgets/ResetSessionPopup";

interface NavBarItem {
    navKey: string;
    labelI18n: string;
    icon: string;
    route: string;
    revaIdRequired?: boolean;
    valueOrderIdRequired?: boolean;
    disabled?: boolean;
    resetId?: boolean;
    permissionsRequired?: PermissionRequirement;
    /** Ohne Berechtigung ausblenden statt als gesperrt (Schloss) anzeigen. */
    hiddenWithoutPermission?: boolean;
    availableSelector?: PlainRevaSelector<boolean>;
    /** Eintrag ausblenden, solange der Selektor true liefert (statt ihn nur zu sperren wie availableSelector). */
    hiddenSelector?: PlainRevaSelector<boolean>;
}

const NAV_ITEMS: NavBarItem[] = [
    {
        navKey: "search",
        // Die Startseite ist seit #19113 unter /start (bare "/" leitet nur dorthin um). Die Route hier
        // MUSS /start sein, sonst matcht die Aktiv-Erkennung (matchPath(route + "/*", pathname)) auf
        // /start/* nicht und der Suche-Button erscheint auf der Startseite fälschlich als "secondary"
        // (ausgegraut statt aktiv). Der Reset-Flow navigiert unabhängig davon weiterhin nach "/".
        labelI18n: "navigation.search",
        route: "/start",
        icon: PrimeIcons.SEARCH,
        revaIdRequired: false,
        resetId: true,
    },
    {
        navKey: "chat",
        labelI18n: "navigation.chat",
        route: "/chat",
        icon: PrimeIcons.COMMENTS,
        revaIdRequired: false,
        permissionsRequired: PERMISSION_RULE_CHAT,
        hiddenWithoutPermission: true,
    },
    {
        navKey: "address",
        labelI18n: "navigation.address",
        route: "/address",
        icon: PrimeIcons.MAP,
        revaIdRequired: true,
    },
    {
        navKey: "location",
        labelI18n: "navigation.location",
        route: "/location",
        icon: PrimeIcons.MAP_MARKER,
        permissionsRequired: PERMISSION_RULE_LOCATION,
        revaIdRequired: true,
    },
    {
        navKey: "market",
        labelI18n: "navigation.market",
        route: "/market",
        icon: PrimeIcons.CHART_LINE,
        permissionsRequired: PERMISSION_RULE_MARKET,
        revaIdRequired: true,
    },
    {
        navKey: "object",
        labelI18n: "navigation.object",
        route: "/object",
        icon: PrimeIcons.BUILDING,
        permissionsRequired: PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS,
        revaIdRequired: true,
    },
    {
        navKey: "value",
        labelI18n: "navigation.value",
        route: "/value",
        icon: PrimeIcons.EURO,
        permissionsRequired: PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS,
        availableSelector: selectAnyValueAvailable,
        revaIdRequired: true,
    },
    {
        navKey: "order",
        labelI18n: "navigation.order",
        route: "/order",
        icon: "pi pi-receipt", // PrimeIcons.RECEIPT is missing from PrimeReact's PrimeIconsOptions type
        revaIdRequired: true,
        // #19481: kein Reiter „Auftrag" bei Zwecken ohne Weitergabe (Research)
        hiddenSelector: (state) => !selectPurposePolicy(state).transferAllowed,
    },
    {
        navKey: "audit",
        labelI18n: "navigation.audit",
        route: "/audit",
        permissionsRequired: PERMISSION_REQUIREMENT_VALUE_NON_SNAPSHOTS,
        icon: PrimeIcons.THUMBS_UP,
        revaIdRequired: true,
        // #19481: kein Reiter „Prüfung" bei Zwecken ohne Prüfbereich (Research)
        hiddenSelector: (state) => !selectPurposePolicy(state).auditAllowed,
    },
];

interface SidebarItemProps {
    item: NavBarItem;
}

/**
 * NavBar item.
 */
function SidebarItem({item}: SidebarItemProps) {
    const {t} = useTranslation();

    const navigate = useNavigateWithQuery();
    const {pathname} = useLocation();

    const resetConfirmRef = useRef<OverlayPanel>(null);

    const available = useRevaSelector(item.availableSelector ?? (() => true));
    const hidden = useRevaSelector(item.hiddenSelector ?? (() => false));

    const revaId = useRevaSelector(selectRevaId);

    const hasValueOrders = useRevaSelector(selectHasValueOrders);

    const {
        navKey,
        labelI18n,
        icon,
        route,
        permissionsRequired,
        hiddenWithoutPermission,
        revaIdRequired,
        valueOrderIdRequired,
        resetId,
    } = item;

    const permitted = useCheckPermissionRequirement(permissionsRequired);

    if ((!permitted && hiddenWithoutPermission) || hidden) {
        return null;
    }

    if (valueOrderIdRequired && !hasValueOrders) {
        // If feature is only relevant for imported Reva-IDs with attached value order ids, hide feature in sidebar
        return null;
    }

    const matchesRoute = !!matchPath(`${route}/*`, pathname);
    const disabled = !route || !available || (revaIdRequired && !revaId);
    const active = !disabled && matchesRoute;

    if (!permitted && !disabled) {
        return (
            <>
                <MissingPermissionTooltip target={`#navButton_${navKey}`} position="right" />
                <Button
                    key={navKey}
                    id={`navButton_${navKey}`}
                    label={t(labelI18n)}
                    icon={PrimeIcons.LOCK}
                    severity="secondary"
                    className="w-full px-1 py-4 text-xs rounded-none border-0 shadow-none text-surface-400 dark:text-surface-400"
                    iconPos="top"
                />
            </>
        );
    }

    if (resetId && revaId) {
        const onResetClick = (event: MouseEvent<HTMLButtonElement>) => {
            if (resetConfirmRef.current) {
                resetConfirmRef.current.toggle(event);
            }
        };

        return (
            <>
                <Button
                    key={navKey}
                    id={`navButton_${navKey}`}
                    label={t(labelI18n)}
                    icon={icon}
                    severity={active ? undefined : "secondary"}
                    disabled={disabled}
                    onClick={onResetClick}
                    className="w-full px-1 py-4 text-xs rounded-none border-0 shadow-none"
                    iconPos="top"
                />
                <ResetSessionPopup ref={resetConfirmRef} />
            </>
        );
    }

    return (
        <Button
            key={navKey}
            id={`navButton_${navKey}`}
            label={t(labelI18n)}
            icon={icon}
            severity={active ? undefined : "secondary"}
            disabled={disabled}
            onClick={() => navigate(route)}
            className="w-full px-1 py-4 text-xs rounded-none border-0 shadow-none"
            iconPos="top"
        />
    );
}

/**
 * Sidebar/Nav-Bar.
 */
export function Sidebar() {
    const isDarkTheme = useRevaSelector(selectDarkTheme);

    return (
        <nav
            className={
                "AppSidebar grow-0 shrink-0 w-20 pt-8 " +
                (isDarkTheme ? "bg-surface-100 dark:bg-surface-700" : "bg-surface-700 dark:bg-surface-100")
            }
        >
            {NAV_ITEMS.map((item) => (
                <SidebarItem key={item.navKey} item={item} />
            ))}
        </nav>
    );
}
